/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { type PacketWorkflow, packetWorkflowsQuery } from '@/api/packets';
import { StartBundle } from '@/components/Bundles';
import { Icon } from '@/components/ui/Icon';
import { Modal } from '@/components/ui/Modal';
import { Empty, Failed, Rows } from '@/components/ui/States';
import { BUNDLE_STEP_DELETED } from '@/lib/deletedTemplates';

/**
 * Starting a packet: which one, then for whom.
 *
 * Opened from New in the top bar. Templates is where packets are set up, not
 * where they are started, so this is the one way in.
 */
export function StartPacket({ onClose }: { onClose: () => void }) {
  const { data, isPending, isError, error, refetch } = useQuery(packetWorkflowsQuery());
  const [chosen, setChosen] = useState<PacketWorkflow | null>(null);

  if (chosen) return <StartBundle bundle={chosen} onClose={onClose} />;

  return (
    <Modal
      title="Start a packet"
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-q" onClick={onClose}>
          Cancel
        </button>
      }
    >
      {isPending ? <Rows rows={3} /> : null}
      {isError ? <Failed error={error} onRetry={() => void refetch()} /> : null}
      {data && data.length === 0 ? (
        <Empty
          icon="inbox"
          title="No packets yet"
          detail="An administrator sets packets up under Templates."
        />
      ) : null}
      {data && data.length > 0 ? (
        <div className="picks">
          {data.map((bundle) => {
            // A step's template was deleted: the server refuses it (ADW-5498).
            const blocked = bundle.step_workflows.some((step) => step.template_retired);
            return (
              <button
                type="button"
                key={bundle.id}
                className="pick"
                aria-label={`Start ${bundle.name}`}
                disabled={blocked}
                onClick={() => {
                  setChosen(bundle);
                }}
              >
                <span className="pick-i" aria-hidden>
                  <Icon name="inbox" size={16} />
                </span>
                <span className="pick-t">
                  <b>{bundle.name}</b>
                  <small>
                    {blocked ? BUNDLE_STEP_DELETED : bundle.steps.join(' → ')}
                  </small>
                </span>
                <span className="pick-a" aria-hidden>
                  <Icon name={blocked ? 'warn' : 'chev'} size={15} />
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </Modal>
  );
}
